import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import type { PlaybackQuality, UserPreferences, VersionChoice } from '../../api/types';
import { useApi } from '../../api/useApi';
import { useDiscoveryState } from '../../api/useDiscoveryState';
import AsyncSection from '../../components/AsyncSection';

/// The choices offered, as one list of "picture, and what it may cost". Encoded into a single
/// value so a select can hold it; the two numbers are what actually travel.
const CHOICES: { key: string; quality: PlaybackQuality }[] = [
  { key: 'original', quality: { maxHeight: null, maxBitrate: null } },
  { key: '2160', quality: { maxHeight: 2160, maxBitrate: null } },
  { key: '1080', quality: { maxHeight: 1080, maxBitrate: null } },
  { key: '1080-8', quality: { maxHeight: 1080, maxBitrate: 8_000_000 } },
  { key: '720', quality: { maxHeight: 720, maxBitrate: null } },
  { key: '720-4', quality: { maxHeight: 720, maxBitrate: 4_000_000 } },
  { key: '720-2', quality: { maxHeight: 720, maxBitrate: 2_000_000 } },
  { key: '480', quality: { maxHeight: 480, maxBitrate: 1_500_000 } },
];

/// What this person wants when they press play, on their own network and away from it. It is a
/// preference and not a capability: what a device can show is what the device reports, this is
/// what somebody decided they want over the line they are on.
export default function PlaybackPreferences() {
  const { t } = useTranslation();
  const preferences = useApi(['users', 'me', 'preferences'], api.myPreferences);
  const discovery = useDiscoveryState();

  const [draft, setDraft] = useState<UserPreferences>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const current = draft ?? preferences.data;

  /// One field changed, the rest as they stand. A preference nobody has touched yet still has to
  /// travel whole: the server is handed all of them or none.
  const changed = (change: Partial<UserPreferences>): UserPreferences => ({
    localQuality: current?.localQuality ?? { maxHeight: null, maxBitrate: null },
    remoteQuality: current?.remoteQuality ?? { maxHeight: null, maxBitrate: null },
    versionChoice: current?.versionChoice,
    ...change,
  });

  const save = async () => {
    if (!current) {
      return;
    }

    setBusy(true);
    setSaved(false);
    setError(undefined);

    try {
      await api.saveMyPreferences(current);

      setDraft(undefined);
      preferences.reload();
      setSaved(true);
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AsyncSection loading={preferences.loading} error={preferences.error} onRetry={preferences.reload}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">{t('settings.saved')}</Alert>}

            <Typography color="text.secondary">{t('settings.playback.intro')}</Typography>

            <QualitySelect
              label={t('settings.playback.local')}
              hint={t('settings.playback.localHint')}
              value={current?.localQuality}
              onChange={(quality) => setDraft(changed({ localQuality: quality }))}
            />

            <QualitySelect
              label={t('settings.playback.remote')}
              hint={t('settings.playback.remoteHint')}
              value={current?.remoteQuality}
              onChange={(quality) => setDraft(changed({ remoteQuality: quality }))}
            />

            {/* Only where it decides anything: on a server without a catalogue that plays on
                demand, no version is ever fetched for somebody to have a say in. */}
            {discovery.playsOnDemand && (
              <TextField
                select
                label={t('settings.playback.versionChoice')}
                helperText={t('settings.playback.versionChoiceHint')}
                value={current?.versionChoice ?? 'Automatic'}
                onChange={(event) =>
                  setDraft(changed({ versionChoice: event.target.value as VersionChoice }))
                }
              >
                <MenuItem value="Automatic">{t('settings.playback.versionChoiceAutomatic')}</MenuItem>
                <MenuItem value="Ask">{t('settings.playback.versionChoiceAsk')}</MenuItem>
              </TextField>
            )}

            <Box>
              <Button variant="contained" onClick={() => void save()} disabled={busy}>
                {t('common.save')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </AsyncSection>
  );
}

function QualitySelect({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: PlaybackQuality | undefined;
  onChange: (quality: PlaybackQuality) => void;
}) {
  const { t } = useTranslation();
  const selected = CHOICES.find((choice) => matches(choice.quality, value)) ?? CHOICES[0];

  return (
    <TextField
      select
      label={label}
      helperText={hint}
      value={selected.key}
      onChange={(event) => {
        const choice = CHOICES.find((candidate) => candidate.key === event.target.value);

        if (choice) {
          onChange(choice.quality);
        }
      }}
    >
      {CHOICES.map((choice) => (
        <MenuItem key={choice.key} value={choice.key}>
          {describe(choice.quality, t)}
        </MenuItem>
      ))}
    </TextField>
  );
}

function matches(left: PlaybackQuality, right: PlaybackQuality | undefined): boolean {
  return Boolean(right) && left.maxHeight === right?.maxHeight && left.maxBitrate === right?.maxBitrate;
}

function describe(quality: PlaybackQuality, t: (key: string) => string): string {
  if (quality.maxHeight === null && quality.maxBitrate === null) {
    return t('player.original');
  }

  const picture = `${quality.maxHeight}p`;

  return quality.maxBitrate
    ? `${picture} · ${(quality.maxBitrate / 1_000_000).toFixed(quality.maxBitrate % 1_000_000 === 0 ? 0 : 1)} Mbit/s`
    : picture;
}
